import { computed, makeObservable } from 'mobx'
import type { Moment } from 'moment'
import moment from 'moment'

import { Model } from 'modules/core/lib/mobx-rest'

export interface PolicyTimelineItemAttrs {
  start_at: string
  end_at: string
  timeoff_policy_id: string
  timeoff_policy_name: string
  policy_assignment_id: string
}

export class PolicyTimelineItem implements PolicyTimelineItemAttrs {
  start_at: string
  end_at: string
  timeoff_policy_id: string
  timeoff_policy_name: string
  policy_assignment_id: string

  constructor(attr: PolicyTimelineItemAttrs) {
    this.start_at = attr.start_at
    this.end_at = attr.end_at
    this.timeoff_policy_id = attr.timeoff_policy_id
    this.timeoff_policy_name = attr.timeoff_policy_name
    this.policy_assignment_id = attr.policy_assignment_id
  }

  get startAt(): Moment {
    return moment(this.start_at)
  }
  get endAt(): Moment {
    return moment(this.end_at)
  }

  get isFuture(): boolean {
    return this.startAt.isAfter(moment(), 'day')
  }

  get isPast(): boolean {
    return this.endAt.isBefore(moment(), 'day')
  }

  get isCurrent(): boolean {
    return this.startAt.isBefore(moment(), 'day') && this.endAt.isSameOrAfter(moment(), 'day')
  }
}

interface Attributes {
  id: string
  employeeId: string
  start_limit_date: Date
  end_limit_date: Date
  items: PolicyTimelineItem[]
}

class PolicyTimeline extends Model<Attributes> {
  constructor(attr: Attributes) {
    super(attr)
    makeObservable(this, {
      items: computed,
    })
  }

  static urlRoot() {
    return '/api/rest/timeoff/policyTimeline'
  }

  get items(): PolicyTimelineItem[] {
    return this.attributes.items
      .slice()
      .map((attrs: PolicyTimelineItemAttrs) => new PolicyTimelineItem(attrs))
      .reverse()
  }

  get current(): PolicyTimelineItem {
    // Find the item that is currently active by comparing the current date to the start and end dates
    const now = moment()

    let item = this.items.find((item) => {
      return now.isBetween(item.startAt, item.endAt, 'day', '[]')
    })

    // If no item is found, find the next item that is in the future. Since this.item is sorted by most recent first
    // we need to reverse the array to find the next item
    if (!item) {
      item = this.items
        .slice()
        .reverse()
        .find((item) => {
          return item.startAt.isAfter(now)
        })
    }

    // If there are no assigments in the future, we return the most recent one
    if (!item) {
      item = this.items[0]
    }
    return item as PolicyTimelineItem
  }
}

export default PolicyTimeline
